import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import api, { inr } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { payForCart } from "@/lib/razorpay";

export default function Checkout() {
  const { items, subtotal, totalBv, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [addr, setAddr] = useState({ line1: "", city: "", state: "", pincode: "" });
  const [useWallet, setUseWallet] = useState(false);

  const shipping = subtotal > 999 ? 0 : 49;
  const total = subtotal + shipping;

  const placeOrder = async () => {
    if (!user) { toast.error("Please login first"); nav("/login"); return; }
    if (items.length === 0) { toast.error("Cart empty"); return; }
    if (!addr.line1 || !addr.city || !addr.pincode) { toast.error("Fill shipping address"); return; }
    setBusy(true);

    if (useWallet) {
      try {
        const { data } = await api.post("/orders", {
          items: items.map((i) => ({ ...i })),
          shipping_address: addr,
          payment_mode: "wallet",
          use_wallet: true,
        });
        toast.success(`Order ${data.order_no} placed!`);
        clear();
        nav("/my-orders");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed");
      } finally { setBusy(false); }
      return;
    }

    payForCart({
      items: items.map((i) => ({ ...i })),
      shipping_address: addr,
      amount: total,
      user,
      onSuccess: (data) => {
        toast.success(`Order ${data.order_no} placed${data.mock ? " (test mode)" : ""}!`);
        clear();
        setBusy(false);
        nav("/my-orders");
      },
      onError: (e) => {
        toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
        setBusy(false);
      },
    });
  };

  return (
    <div className="min-h-screen bg-sand">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-serif text-4xl text-navy" data-testid="checkout-title">Checkout</h1>
        {!user && <div className="mt-6 bg-cream border border-gold/30 p-4 rounded-sm text-sm">Please <Link to="/login" className="text-gold underline">login</Link> to checkout.</div>}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="space-y-6">
            <div className="bg-white border border-border p-6 rounded-sm">
              <h3 className="font-serif text-xl text-navy mb-4">Shipping address</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="addr-line1" placeholder="Address line" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} className="sm:col-span-2 border border-border px-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none" />
                <input data-testid="addr-city" placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className="border border-border px-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none" />
                <input data-testid="addr-state" placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} className="border border-border px-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none" />
                <input data-testid="addr-pincode" placeholder="Pincode" value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} className="border border-border px-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none" />
              </div>
            </div>
            <div className="bg-white border border-border p-6 rounded-sm">
              <h3 className="font-serif text-xl text-navy mb-4">Payment</h3>
              <label className="flex items-center gap-3 cursor-pointer text-sm">
                <input type="radio" checked={!useWallet} onChange={() => setUseWallet(false)} /> Razorpay / UPI / Card (auto-detects test mode)
              </label>
              {user && (
                <label className="flex items-center gap-3 cursor-pointer text-sm mt-3">
                  <input data-testid="use-wallet" type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} /> Pay using Income Wallet
                </label>
              )}
            </div>
          </div>
          <aside className="bg-white border border-border p-6 rounded-sm h-fit">
            <h3 className="font-serif text-xl text-navy mb-4">Order summary</h3>
            <ul className="divide-y divide-border text-sm">
              {items.map((i) => (
                <li key={i.product_id} className="py-3 flex justify-between">
                  <span className="text-navy/70 truncate pr-4">{i.name} × {i.qty}</span>
                  <span>{inr(i.mrp * i.qty)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-navy/60">Subtotal</span><span>{inr(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-navy/60">Shipping</span><span>{shipping === 0 ? "Free" : inr(shipping)}</span></div>
              <div className="flex justify-between text-gold-deep"><span>Total SP</span><span>{totalBv}</span></div>
              <div className="border-t border-border pt-2 text-navy flex justify-between items-baseline"><span className="font-serif text-xl">Total</span><span data-testid="checkout-total" className="text-xl font-extrabold tabular-nums lining-nums">{inr(total)}</span></div>
            </div>
            <button data-testid="place-order-btn" onClick={placeOrder} disabled={busy} className="mt-6 w-full bg-navy text-white py-3 hover:bg-navy-deep rounded-sm disabled:opacity-50">
              {busy ? "Processing..." : useWallet ? "Pay from Wallet" : "Pay & Place Order"}
            </button>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
